import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, Search, CreditCard, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { createPaymentSession } from '../../services/api/payment';
import { useGeoLocation } from '../../hooks/useGeoLocation'; 
import { useRegionalPricing } from '../../hooks/useRegionalPricing';
import { useSearchLimits } from '../../hooks/useSearchLimits';
import { SEO } from '../seo/SEO';
import { PaymentModal } from './PaymentModal';
import { Link } from 'react-router-dom'; 

interface ActiveSubscription {
  subscription_id: string;
  plan_name: string;
  credits_remaining: number;
  ends_at: string;
  status: string;
}
 
interface Plan {
  id: string;
  name: string;
  description: string;
  credits: number;
  duration_days: number;
  price: number;
  currency_code: string;
  currency_symbol: string;
  is_active: boolean;
  is_popular: boolean;
}

export const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { countryCode, loading: locationLoading } = useGeoLocation();
  const { plans, isLoading: plansLoading } = useRegionalPricing(countryCode);
  const { remainingSearches, isLoading: limitsLoading } = useSearchLimits();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Fetch active or expired/cancelled subscription
  const { data: activeSubscription, isLoading: subscriptionLoading } = useQuery<ActiveSubscription>(
    ['active-subscription', user?.id],
    async () => {
      // First check for active subscription
      const { data: activeData, error: activeError } = await supabase
        .rpc('get_active_subscription', {
          user_id: user?.id
        });

      if (activeError) throw activeError;
      
      // If we have an active subscription, return it
      if (activeData && activeData[0]) {
        return activeData[0];
      }      
      return null;
    },
    {
      enabled: !!user?.id,
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: true
    }
  );

  const handleSubscribe = (plan: Plan) => {
    setError(null);
    setSelectedPlan(plan);
    setShowPaymentModal(true);
    setRetryCount(0);
  };

  const handleSelectPaymentMethod = async (methodId: string, gatewayId: string) => {
    if (!selectedPlan) return;
    
    try {
      setError(null);
      setIsProcessing(true);
      setShowPaymentModal(false);
      
      await createPaymentSession(
        selectedPlan.price,
        `${selectedPlan.name} - ${selectedPlan.credits} credits`,
        selectedPlan.currency_code,
        methodId,
        gatewayId,
        selectedPlan.id
      );
    } catch (error) {
      console.error('Payment error:', error);
      
      let errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      // Enhance error message based on known error patterns
      if (errorMessage.includes('network')) {
        errorMessage = 'Unable to connect to payment service. Please check your internet connection and try again.';
      } else if (errorMessage.includes('timeout')) {
        errorMessage = 'The payment request is taking longer than expected. You can try again.';
      } else if (errorMessage.includes('rate limit')) {
        errorMessage = 'Too many payment attempts. Please wait a moment before trying again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetryPayment = () => {
    if (!selectedPlan) return;
    setRetryCount(prev => prev + 1);
    setShowPaymentModal(true);
    setError(null);
  };

  // Format price with commas for thousands
  const formatPrice = (price: number): string => {
    return Math.floor(price / 100).toLocaleString();
  };

  if (!user) {
    navigate('/signin');
    return null;
  }

  const isLoading = locationLoading || plansLoading || subscriptionLoading || limitsLoading;

  return (
    <>
      <SEO 
        title="Billing & Subscriptions | Banga.ai"
        description="Choose a subscription plan to get more search credits and unlock premium features."
      />
      
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Subscription Plans</h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {activeSubscription 
                ? "Manage your subscription or purchase additional plans" 
                : "Choose the perfect plan for your needs"}
            </p>
          </div>

          {/* Error Message with Retry Button */}
          {error && (
            <div className="mt-8 max-w-2xl mx-auto">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                  {selectedPlan && (
                    <button
                      onClick={handleRetryPayment}
                      className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-md transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry Payment
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Processing Indicator */}
          {isProcessing && (
            <div className="mt-8 max-w-2xl mx-auto">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-3 text-blue-700 dark:text-blue-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <p>Processing your payment...</p>
                </div>
              </div>
            </div>
          )}

          {/* Active/Expired Subscription */}
          {isLoading && (
            <div className="mt-8 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          )}
          
          {!isLoading && activeSubscription && (
            <div className="mt-8 max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-lg p-6 border border-blue-100 dark:border-blue-900/30 transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
                      <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {activeSubscription.plan_name}
                      </h2>
                      {activeSubscription.status === 'active' ? (
                        <p className="text-sm text-green-600 dark:text-green-400">
                          Active Subscription
                        </p>
                      ) : activeSubscription.status === 'expired' ? (
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                          Subscription Expired
                        </p>
                      ) : (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          Cancelled
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-5 shadow-sm border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                        <Search className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Credits Remaining
                      </h3>
                    </div>
                    <div className="flex items-end gap-2">
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {activeSubscription.credits_remaining.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">searches</p>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-5 shadow-sm border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-full">
                        <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {activeSubscription.status === 'active' ? 'Subscription Expires' : 'Expired On'}
                      </h3>
                    </div>
                    <div className="flex items-end gap-2">
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {new Date(activeSubscription.ends_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                        {new Date(activeSubscription.ends_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-blue-100 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {activeSubscription.status === 'active'
                      ? 'Need more credits? You can purchase additional plans below.'
                      : activeSubscription.status === 'cancelled'
                        ? 'Your subscription has been cancelled but remains active until the expiry date. Purchase a new plan below to continue after expiry.'
                        : 'Your subscription has expired. Please purchase a new plan below to continue using premium features.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {!isLoading && !activeSubscription && user && (
            // No Subscription (New User)
            <div className="mt-8 max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-lg p-6 border border-amber-100 dark:border-amber-900/30 transition-all">
                <div className="flex items-center gap-4">
                  <div className="bg-amber-100 dark:bg-amber-900/30 p-3 rounded-full">
                    <Shield className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      No Active Subscription
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Choose a plan below to get more search credits
                    </p>
                  </div>
                </div>
                
                <div className="mt-6 bg-white dark:bg-gray-700 rounded-lg p-5 shadow-sm border border-gray-100 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                      <Search className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Free Credits</h3>
                  </div>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {remainingSearches !== null ? remainingSearches.toLocaleString() : '0'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">searches remaining</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Plans */}
          <div className={`${activeSubscription ? 'mt-8' : 'mt-12'} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}>
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                </div>
              ))
            ) : (
              plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden relative ${
                    plan.is_popular ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  {plan.is_popular && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-blue-500 text-white px-4 py-1 rounded-bl-lg text-sm font-medium">
                        Popular
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {plan.name}
                    </h3>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">
                      {plan.description}
                    </p>
                    <p className="mt-6 text-5xl font-extrabold text-gray-900 dark:text-white">
                      {plan.currency_symbol}{formatPrice(plan.price)}
                    </p>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {plan.duration_days === 1 ? '24-hour access' :
                       plan.duration_days === 7 ? '7-day access' :
                       `${plan.duration_days}-day access`}
                    </p>
                  </div>
                  <div className="px-6 pt-6 pb-8">
                    <ul className="space-y-4">
                      <li className="flex items-center">
                        <Search className="w-5 h-5 text-green-500 mr-3" />
                        <span className="text-gray-700 dark:text-gray-300">
                          {plan.credits.toLocaleString()} searches
                        </span>
                      </li>
                      <li className="flex items-center">
                        <Clock className="w-5 h-5 text-green-500 mr-3" />
                        <span className="text-gray-700 dark:text-gray-300">
                          {plan.duration_days === 1 ? '24-hour validity' :
                           plan.duration_days === 7 ? '7-day validity' :
                           `${plan.duration_days}-day validity`}
                        </span>
                      </li>
                    </ul>
                    <button
                      onClick={() => handleSubscribe(plan)}
                      disabled={isProcessing}
                      className={`mt-6 w-full rounded-lg px-4 py-3 flex items-center justify-center gap-2 ${
                        isProcessing
                          ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                          : plan.is_popular 
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                      } transition-colors`}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5" />
                          <span>Get Access Now</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Payment Method Selection Modal */}
      {selectedPlan && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setError(null);
          }}
          onSelectPaymentMethod={handleSelectPaymentMethod}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price}
          planCredits={selectedPlan.credits}
          currencyCode={selectedPlan.currency_code}
          currencySymbol={selectedPlan.currency_symbol}
          regionCode={countryCode}
          planId={selectedPlan.id}
          isProcessing={isProcessing}
        />
      )}
    </>
  );
};